/**
 * Portal shell: auth gate, role lookup, data load, then the views that role
 * is allowed to see.
 *
 *   manager     Management · Coach payouts · Class performance · City View · Settings
 *   city_view   City View only
 *   frontdesk   refused — it is a sync account, not a login for these pages
 *
 * The tab list is filtered by role, and every route re-checks — typing
 * /portal/coaches as City View lands on their own page rather than the payouts.
 *
 * Both boundaries are enforced by Postgres, not by these tabs. City View and
 * managers cannot insert a member or a payment, and Settings would 403 for City
 * View. City View also cannot READ the tables at all: its page is built from
 * the `city_view_report` RPC, which returns totals and the member list and
 * nothing else (supabase/migrations/0007), so coach pay and the per-class money
 * are withheld by the database rather than merely left off the screen.
 *
 * Everything below the auth gate is lazy-loaded so Recharts (~430KB) is
 * fetched only when someone signs in, and never lands in the front desk's
 * offline precache.
 */
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { getSupabase, isSupabaseConfigured } from '@shared/supabase';
import { loadPortalData, loadUserRole, type PortalData, type PortalRole } from './data';
import { computePayouts, MANAGER_COACH_NAME } from '@shared/payouts';
import type { DateRange } from '@shared/analytics';
import {
  DEFAULT_SPLIT,
  parseCoachRates,
  type CoachRates,
  type SplitConfig,
} from '@shared/payouts';
import Login from './Login';

const ManagementDashboard = lazy(() => import('./Dashboard'));
const CoachDashboard = lazy(() => import('./CoachDashboard'));
const CityViewDashboard = lazy(() => import('./CityViewDashboard'));
const ClassPerformance = lazy(() => import('./ClassPerformance'));
const SettingsView = lazy(() => import('./SettingsView'));

type State =
  | { phase: 'checking' }
  | { phase: 'signedOut' }
  | { phase: 'loading'; email: string }
  | { phase: 'ready'; email: string; role: PortalRole; data: PortalData }
  | { phase: 'error'; email: string; message: string };

const ALL_TABS = [
  { to: '/portal', label: 'Management', end: true, roles: ['manager'] },
  { to: '/portal/coaches', label: 'Coach payouts', end: false, roles: ['manager'] },
  { to: '/portal/performance', label: 'Class performance', end: false, roles: ['manager'] },
  {
    to: '/portal/city-view',
    label: 'City View',
    end: false,
    roles: ['manager', 'city_view'],
  },
  { to: '/portal/settings', label: 'Settings', end: false, roles: ['manager'] },
] as const;

export default function PortalApp() {
  const [state, setState] = useState<State>({ phase: 'checking' });
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);

  /**
   * The role first, then the tables — and the tables only for the manager.
   *
   * City View holds no SELECT on any table; its page reads its own report
   * (portal/cityViewReport.ts). Loading the tables for it would return nothing
   * but empty arrays, so it gets an empty dataset and never asks.
   */
  const fetchFor = useCallback(async () => {
    const role = await loadUserRole();
    const data = role && role !== 'city_view' ? await loadPortalData() : EMPTY_DATA;
    return { role, data };
  }, []);

  const load = useCallback(async (email: string) => {
    setState({ phase: 'loading', email });
    try {
      const { role, data } = await fetchFor();
      if (!role) {
        throw new Error(
          'This account has no role assigned. Add a row to user_roles in Supabase.'
        );
      }
      setState({ phase: 'ready', email, role, data });
    } catch (e) {
      setState({ phase: 'error', email, message: e instanceof Error ? e.message : String(e) });
    }
  }, [fetchFor]);

  /**
   * Re-read everything WITHOUT dropping back to the splash screen.
   *
   * The portal used to load once at sign-in and then sit there. Leave the tab
   * open while the front desk enters a member and you would see nothing change
   * — which is indistinguishable from sync being broken, and cost real time
   * diagnosing exactly that. Now it reloads when the tab regains focus and
   * every minute while it is visible, and says when it last looked.
   */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const { role, data } = await fetchFor();
      if (role) {
        setState((prev) => (prev.phase === 'ready' ? { ...prev, role, data } : prev));
        setLastLoaded(new Date());
      }
    } catch {
      // A failed background refresh must not blank the page the manager is
      // reading. The stale-but-rendered data stays, and the timestamp stops
      // advancing — which is the honest signal that something is wrong.
    } finally {
      setRefreshing(false);
    }
  }, [fetchFor]);

  useEffect(() => {
    if (state.phase !== 'ready') return;

    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 60_000);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [state.phase, refresh]);

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) {
      setState({ phase: 'signedOut' });
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      const email = data.session?.user.email;
      if (email) void load(email);
      else setState({ phase: 'signedOut' });
    });
  }, [load]);

  const signOut = useCallback(async () => {
    await getSupabase()?.auth.signOut();
    setState({ phase: 'signedOut' });
  }, []);

  if (state.phase === 'checking' || state.phase === 'loading') return <Splash />;

  if (state.phase === 'signedOut') {
    return (
      <Login
        onSignedIn={async () => {
          const { data } = (await getSupabase()!.auth.getSession()) ?? {};
          void load(data?.session?.user.email ?? '');
        }}
      />
    );
  }

  if (state.phase === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink p-6">
        <div className="card max-w-lg p-8 text-center">
          <h1 className="mb-3 text-2xl text-brand">Could not load the data</h1>
          <p className="mb-4 text-sm text-white/50">
            {isSupabaseConfigured
              ? 'Every account needs a row in user_roles. See SETUP.md.'
              : 'The cloud is not configured for this deployment.'}
          </p>
          <pre className="mb-6 overflow-x-auto rounded-btn bg-black p-4 text-left text-xs text-status-expired">
            {state.message}
          </pre>
          <button onClick={signOut} className="btn-ghost">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const { data, email, role } = state;

  // The front desk account exists to sync one PC. It is not a login for these
  // pages, and letting it in here would put payout figures on any screen where
  // someone knows the desk password — which is the one password the whole
  // reception team shares.
  //
  // Be precise about what this is: an APP-level refusal, not a database one.
  // That account must keep SELECT rights or the front desk could not pull, so
  // this cannot be a Postgres policy. It stops the portal being used with the
  // shared password; it is not a wall against someone calling the API directly.
  if (role === 'frontdesk') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink p-6">
        <div className="card max-w-lg p-8 text-center">
          <img src="/logo.png" alt="" className="mx-auto mb-5 h-16 w-auto" />
          <h1 className="mb-3 text-2xl text-brand">Not a portal account</h1>
          <p className="mb-6 text-sm leading-relaxed text-white/60">
            <strong className="text-white/80">{email}</strong> is the front desk sync account.
            It keeps the reception PC talking to the cloud and has no access to these pages.
            <br />
            <br />
            Sign in with the manager or City View account instead.
          </p>
          <button onClick={signOut} className="btn">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const tabs = ALL_TABS.filter((t) => (t.roles as readonly string[]).includes(role));
  const home = role === 'city_view' ? '/portal/city-view' : '/portal';

  // The split comes from the synced settings, so the portal and the front desk
  // always agree on how the money divides.
  // A setting that is missing, blank or unparseable falls back to the default
  // rather than to NaN: Number('') is 0, and a 0% share would quietly pay
  // somebody nothing instead of showing an error anyone would notice.
  const percent = (raw: string | undefined, fallback: number) => {
    const n = Number(raw);
    return raw !== undefined && raw.trim() !== '' && Number.isFinite(n) ? n : fallback;
  };

  const split: SplitConfig = {
    coachPercent: percent(data.settings.coach_percent, DEFAULT_SPLIT.coachPercent),
    managerPercent: percent(data.settings.manager_percent, DEFAULT_SPLIT.managerPercent),
    cityViewPercent: percent(data.settings.city_view_percent, DEFAULT_SPLIT.cityViewPercent),
  };

  // The coaches on a rate of their own. Same blob the front desk writes, so
  // both agree on what each person is owed.
  const coachRates: CoachRates = parseCoachRates(data.settings.coach_rates);

  // City View's cut as the coach payouts page computes it, so the venue's
  // server-side figure can be checked against it on the manager's screen.
  const cityViewCrossCheck = (range: DateRange) =>
    computePayouts(
      data.payments,
      data.subscriptions,
      data.walkIns,
      range,
      data.coachAssignments,
      split,
      MANAGER_COACH_NAME,
      [],
      coachRates
    ).cityViewTotal;

  const ROLE_LABEL: Record<PortalRole, string> = {
    frontdesk: 'Front desk',
    manager: 'Manager',
    city_view: 'City View',
  };

  return (
    <div className="min-h-screen bg-ink">
      <header className="sticky top-0 z-40 border-b-2 border-brand bg-black/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="" className="h-11 w-auto" />
            <div>
              <div className="font-display text-lg leading-none tracking-display">
                SK MARTIAL ARTS
              </div>
              <div className="text-[10px] uppercase tracking-button text-white/40">
                City View Branch · {ROLE_LABEL[role]}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-white/40 sm:inline">{email}</span>
            {lastLoaded && (
              <span className="hidden text-xs text-white/30 md:inline">
                updated {lastLoaded.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={() => void refresh()}
              disabled={refreshing}
              title={
                lastLoaded
                  ? `Last updated ${lastLoaded.toLocaleTimeString()}`
                  : 'Reload from the cloud'
              }
              className="rounded-btn border border-edge px-4 py-2 text-xs font-bold uppercase tracking-button hover:border-brand disabled:opacity-40"
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              onClick={signOut}
              className="rounded-btn border border-edge px-4 py-2 text-xs font-bold uppercase tracking-button hover:border-brand"
            >
              Sign out
            </button>
          </div>
        </div>

        {tabs.length > 1 && (
          <nav className="mx-auto flex max-w-7xl gap-1 overflow-x-auto border-t border-edge px-4 py-2">
            {tabs.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-btn px-4 py-2 text-xs font-bold uppercase tracking-button transition-all duration-brand ${
                    isActive ? 'bg-brand text-paper' : 'text-white/50 hover:text-paper'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <Suspense fallback={<Splash />}>
        <Routes>
          <Route
            path="/"
            element={
              role === 'city_view' ? (
                <Navigate to="/portal/city-view" replace />
              ) : (
                <ManagementDashboard data={data} onVoided={refresh} />
              )
            }
          />
          <Route
            path="/coaches"
            element={
              role === 'city_view' ? (
                <Navigate to="/portal/city-view" replace />
              ) : (
                <CoachDashboard
                  data={data}
                  coachAssignments={data.coachAssignments}
                  split={split}
                  coachRates={coachRates}
                />
              )
            }
          />
          <Route
            path="/performance"
            element={
              role === 'city_view' ? (
                <Navigate to="/portal/city-view" replace />
              ) : (
                <ClassPerformance data={data} />
              )
            }
          />
          <Route
            path="/city-view"
            element={
              <CityViewDashboard
                // Remounts on Refresh and on the background reload, so the
                // venue's report is re-fetched like everything else.
                key={lastLoaded?.getTime() ?? 0}
                crossCheck={role === 'manager' ? cityViewCrossCheck : undefined}
              />
            }
          />
          <Route
            path="/settings"
            element={
              role === 'city_view' ? (
                <Navigate to="/portal/city-view" replace />
              ) : (
                <SettingsView onChanged={refresh} />
              )
            }
          />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

const EMPTY_DATA: PortalData = {
  members: [],
  subscriptions: [],
  payments: [],
  walkIns: [],
  freezes: [],
  coachAssignments: {},
  roster: [],
  settings: {},
};

function Splash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink">
      <img src="/logo.png" alt="" className="h-20 w-auto animate-pulse" />
    </div>
  );
}
